import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import { ARIA_ROLES } from 'shared/constants';
import useTranslation from 'src/hooks/use-translation';
import PinIcon from 'assets/pin-icon';
import { Button } from 'components/core';
import {
  AddressAutocompleteGeolocateButton,
  AddressAutocompleteInput,
  AddressAutocompleteOptionsList,
  AddressAutocompletePopover,
} from 'src/components/address-autocomplete';

import { OrderTypeTabs } from '../components/desktop-address-combobox.tsx/order-type-tabs';
import { useDesktopAddressCombobox } from '../components/desktop-address-combobox.tsx/use-desktop-address-combobox';

export const DesktopAddressComboboxDark = observer(
  (): JSX.Element => {
    const { t } = useTranslation();

    const {
      onChangeOrderType,
      onStartShoppingClick,
      orderType,
      errorMessage,
      onInputChange,
    } = useDesktopAddressCombobox();

    const inputPlaceHolder = t('home.inputPlaceholder', 'Enter your address or city to get started...');
    const ctaText = t('home.ctaText', 'Start shopping');

    return (
      <Wrapper>
        <InputWrapper>
          <StyledPinIcon role={ARIA_ROLES.IMG} title={t('homepage.pin-icon', 'Pin locator icon')} />
          <StyledInput placeholder={inputPlaceHolder} onChange={onInputChange} data-testid='homeAddressInput' />
        </InputWrapper>
        <StyledPopover
          $error={!!errorMessage}
          aria-label={t('homepage.dispensary-combobox', 'Dispensaries near this address')}
        >
          <OrderTypeTabs value={orderType} onChange={onChangeOrderType} />
          <StyledGeolocateButton />
          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
          <StyledList wrapText={false} />
        </StyledPopover>
        <StartShoppingButton variant='flora' onClick={onStartShoppingClick}>
          {ctaText}
        </StartShoppingButton>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  max-width: 696px;
  width: 100%;
  position: relative;
`;

const InputWrapper = styled.div`
  margin-top: 0;
  height: 74px;
  max-width: 696px;
  position: relative;
`;

const StyledInput = styled(AddressAutocompleteInput)`
  font-size: 17px;
  height: 100%;
  width: 100%;
  border: 1px solid var(--rds-input-border);
  background-color: var(--rds-input-bg);
  color: var(--rds-cream);
  border-radius: 12px;
  padding: 1px 158px 0 68px;

  &::placeholder {
    color: var(--rds-input-placeholder);
  }
`;

const StyledPinIcon = styled(PinIcon)`
  height: 27px;
  position: absolute;
  left: 29px;
  top: 50%;
  transform: translateY(-50%);
  fill: var(--rds-gold-first);
`;

const StyledGeolocateButton = styled(AddressAutocompleteGeolocateButton)`
  padding: 15px 16px;
  background-color: var(--rds-border);
  color: var(--rds-cream);
  border-radius: 8px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }

  svg path {
    fill: var(--rds-gold-first);
  }

  svg {
    width: 21px;
    height: 21px;
  }
`;

const StyledList = styled(AddressAutocompleteOptionsList)`
  color: var(--rds-cream);

  [aria-selected='true'] {
    background: rgba(255, 255, 255, 0.08);
    box-shadow: none;
  }
`;

const ErrorText = styled.div`
  padding: 20px 14px 0;
  color: ${({ theme }) => theme.colors.red[55]};
`;

const StartShoppingButton = styled(Button)`
  position: absolute;
  text-transform: none;
  bottom: 18px; // 74px is the height of the input
  right: 18px;

  &&& {
    background-color: var(--rds-gold);
    color: var(--rds-text-on-gold);

    &:hover {
      background-color: var(--rds-gold-bright);
    }
  }
`;

const StyledPopover = styled(AddressAutocompletePopover)<{ $error?: boolean }>`
  width: 696px;
  margin-top: 2px;
  padding: 21px 22px 22px;
  border-radius: 12px;
  position: absolute;
  background-color: var(--rds-bg-card);
  border: 1px solid var(--rds-input-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);

  &&& * {
    font-size: 15px;
  }

  /* Tab bar */
  .MuiTab-textColorInherit {
    color: var(--rds-cream-dim);
    opacity: 1;
  }
  .MuiTab-textColorInherit.Mui-selected {
    color: var(--rds-cream);
  }
  .MuiTabs-indicator {
    background-color: var(--rds-gold);
  }
  .MuiTabs-root {
    border-bottom: 1px solid var(--rds-border-subtle);
  }

  /* Option items */
  [role='option'] {
    color: var(--rds-cream);
    border-radius: 8px;

    &:hover {
      background: var(--rds-border);
      cursor: pointer;
    }

    svg {
      fill: var(--rds-cream-dim);
    }
  }

  [data-highlighted] {
    background: var(--rds-border);
  }

  ${({ $error, theme }) => $error && `box-shadow: 0 0 0pt 1pt ${theme.colors.red[55] as string}`};
`;
